/**
 * El catálogo de la caja al día sin que nadie pulse «Sincronizar».
 *
 * Al abrir el POS pregunta una vez al servidor con la huella del catálogo que tiene
 * (`GET pos/catalogo/{sucursal}?huella=`, `App\Domains\Pos` en el backend). Si nada cambió no baja
 * el catálogo; si cambió un producto, un precio, un medio de pago..., llega entero y se reemplaza de
 * una vez, sin recargar la pantalla. Entero y no por partes a propósito: así no hay que llevar la
 * cuenta de qué se borró, y comprimido pesa poco (1,2 MB con 15.600 productos). No revisa durante el
 * día: lo que cambie en el ERP con la caja abierta llega al volver a abrir o con «Sincronizar».
 *
 * La misma respuesta trae siempre las existencias de la bodega de la caja (plugins/pos/existencias.js);
 * después, cada venta trae las de lo vendido. Si la sucursal tiene más de una caja, lo que venden las
 * otras no llega con las ventas propias: entonces cada 5 minutos pregunta sólo qué existencias
 * cambiaron (`GET pos/existencias/{sucursal}?desde=`). El servidor dice si hay que preguntar (`cada`).
 * Sin internet no pregunta, y el aviso sigue con lo que la caja tiene guardado.
 */
import bus from '@/plugins/bus-manager';
import { aplicarExistencias, asegurarColecciones, revisionDeExistencias } from './existencias';

const CLAVE_HUELLA = 'pos_catalogo_huella';
// Después de lo que el POS pide al arrancar.
const ESPERA_AL_ABRIR = 10 * 1000;
// Cada cuánto se mira si ya toca preguntar por las existencias (el plazo lo pone el servidor).
const PULSO = 60 * 1000;

export function guardarHuellaDelCatalogo(huella) {
  if (huella) {
    localStorage.setItem(CLAVE_HUELLA, huella);
  } else {
    localStorage.removeItem(CLAVE_HUELLA);
  }
}

export default class CatalogoAlDia {
  constructor(db, store, http, sourceConfig, pos) {
    this.db = db;
    this.store = store;
    this.http = http;
    this.sourceConfig = sourceConfig;
    this.pos = pos;
    this.iniciado = false;
    this.revisando = false;
    this.ultimaDeExistencias = Date.now();
    bus.$on('existencias', (existencias) => aplicarExistencias(this.db, existencias));
  }

  /** Lo llama Home cuando la caja está instalada: una revisión por apertura. */
  iniciar() {
    if (this.iniciado) {
      return;
    }
    this.iniciado = true;
    setTimeout(() => this.revisar(), ESPERA_AL_ABRIR);
    setInterval(() => this.revisarExistencias(), PULSO);
  }

  async revisar() {
    const installation = this.store.getters.installation;
    if (this.revisando || !this.store.getters.online || !installation || !installation.subsidiary_id) {
      return;
    }
    this.revisando = true;
    try {
      const huella = encodeURIComponent(localStorage.getItem(CLAVE_HUELLA) || '');
      const r = await this.http.get(`api/v1/pos/catalogo/${installation.subsidiary_id}?huella=${huella}`, null, false, false, false);
      if (r && r.success) {
        if (!r.sin_cambios && r.config) {
          await this.aplicar(r.config);
          guardarHuellaDelCatalogo(r.huella);
        }
        aplicarExistencias(this.db, r.existencias);
        this.ultimaDeExistencias = Date.now();
        // La app de escritorio guarda su numeración para vender sin conexión.
        bus.$emit('ajustes-sin-conexion', r.sin_conexion);
      }
    } catch (e) {
      // sin conexión o el servidor no contestó: queda lo que había
      console.log('catálogo: no se pudo revisar', e && e.message);
    } finally {
      this.revisando = false;
    }
  }

  /** Sólo en sucursales con varias cajas: lo que cambió desde la última vez. */
  async revisarExistencias(forzar = false) {
    const { desde, cada } = revisionDeExistencias();
    const installation = this.store.getters.installation;
    if (!cada || this.revisando || !this.store.getters.online || !installation || !installation.subsidiary_id) {
      return;
    }
    if (!forzar && Date.now() - this.ultimaDeExistencias < cada * 1000) {
      return;
    }
    this.revisando = true;
    this.ultimaDeExistencias = Date.now();
    try {
      const r = await this.http.get(`api/v1/pos/existencias/${installation.subsidiary_id}?desde=${encodeURIComponent(desde)}`, null, false, false, false);
      if (r && r.success) {
        aplicarExistencias(this.db, r.existencias);
      }
    } catch (e) {
      console.log('existencias: no se pudo revisar', e && e.message);
    } finally {
      this.revisando = false;
    }
  }

  /**
   * Reemplaza cada colección que trae el servidor. Todo en el mismo paso (LokiJS es síncrono), así
   * la pantalla nunca ve el catálogo a medio cambiar; lo que ya está en el carrito es una copia y
   * conserva su precio.
   */
  async aplicar(config) {
    const loki = this.db.adapter.db;
    asegurarColecciones(loki);
    for (const nombre of Object.keys(config)) {
      const items = config[nombre];
      const coleccion = Array.isArray(items) ? loki.getCollection(nombre) : null;
      if (!coleccion) {
        continue;
      }
      coleccion.findAndRemove({});
      if (items.length) {
        coleccion.insert(items);
      }
    }
    // Lo que el POS tiene en memoria desde que abrió.
    this.pos.priceLists = await this.db.getPriceLists();
    this.sourceConfig.map(await this.db.adapter.getConfigurations());
    bus.$emit('catalogo-actualizado');
  }
}
